import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { CalculatorInput, CalculatorResult, CalculatorResultsGrid } from '@/components/calculators'
import {
  calculateMonthlyPayment,
  calculateFutureValue,
  calculateRemainingBalance,
  formatCurrency,
} from '@/lib/calculators/formulas'

export default function RentVsBuyCalculator() {
  const [monthlyRent, setMonthlyRent] = useState(1500)
  const [annualRentIncrease, setAnnualRentIncrease] = useState(3)
  const [purchasePrice, setPurchasePrice] = useState(250000)
  const [downPaymentPercent, setDownPaymentPercent] = useState(20)
  const [interestRate, setInterestRate] = useState(7)
  const [loanTerm] = useState(30)
  const [propertyTaxRate, setPropertyTaxRate] = useState(1.5)
  const [maintenanceRate, setMaintenanceRate] = useState(1)
  const [insuranceAnnual, setInsuranceAnnual] = useState(1500)
  const [appreciationRate, setAppreciationRate] = useState(3)
  const [investmentReturn, setInvestmentReturn] = useState(7)
  const [holdYears, setHoldYears] = useState(7)

  const downPayment = purchasePrice * (downPaymentPercent / 100)
  const loanAmount = purchasePrice - downPayment
  const monthlyPI = calculateMonthlyPayment(loanAmount, interestRate, loanTerm)
  const monthlyTaxes = (purchasePrice * propertyTaxRate) / 100 / 12
  const monthlyInsurance = insuranceAnnual / 12
  const monthlyMaintenance = (purchasePrice * maintenanceRate) / 100 / 12
  const totalMonthlyOwnership = monthlyPI + monthlyTaxes + monthlyInsurance + monthlyMaintenance

  // Total cost of renting over hold period (with annual increases)
  let totalRentCost = 0
  let currentRent = monthlyRent
  for (let year = 0; year < holdYears; year++) {
    totalRentCost += currentRent * 12
    currentRent *= 1 + annualRentIncrease / 100
  }

  // Equity at end of hold period
  const futureValue = calculateFutureValue(purchasePrice, appreciationRate, holdYears)
  const remainingBalance = calculateRemainingBalance(loanAmount, interestRate, loanTerm, holdYears * 12)
  const equity = futureValue - remainingBalance

  // Opportunity cost: if you invested the down payment instead
  const downPaymentGrowth = downPayment * Math.pow(1 + investmentReturn / 100, holdYears)

  const buyingIsBetter = equity > downPaymentGrowth

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 py-12">
      <Link to="/resources/calculators" className="text-amber-600 hover:underline">
        &larr; Back to Calculators
      </Link>
      <h1 className="text-3xl font-bold">Rent vs Buy Calculator</h1>

      <Card>
        <CardContent className="p-6">
          <p>
            Compare the total cost of renting versus buying over a given time period. This calculator accounts for rent
            increases, home appreciation, equity buildup, and the opportunity cost of your down payment.
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-4 p-6">
            <h2 className="text-lg font-semibold">Renting</h2>

            <CalculatorInput label="Current Monthly Rent" value={monthlyRent} onChange={setMonthlyRent} prefix="$" />

            <CalculatorInput
              label="Annual Rent Increase"
              value={annualRentIncrease}
              onChange={setAnnualRentIncrease}
              suffix="%"
            />

            <CalculatorInput
              label="Investment Return (on savings)"
              value={investmentReturn}
              onChange={setInvestmentReturn}
              suffix="%"
              tooltip="If you rent and invest the down payment instead"
            />

            <Separator />

            <h2 className="text-lg font-semibold">Buying</h2>

            <CalculatorInput label="Purchase Price" value={purchasePrice} onChange={setPurchasePrice} prefix="$" />

            <CalculatorInput
              label="Down Payment"
              value={downPaymentPercent}
              onChange={setDownPaymentPercent}
              suffix="%"
            />

            <CalculatorInput label="Interest Rate" value={interestRate} onChange={setInterestRate} suffix="%" />

            <CalculatorInput
              label="Property Tax Rate"
              value={propertyTaxRate}
              onChange={setPropertyTaxRate}
              suffix="%"
            />

            <CalculatorInput
              label="Annual Insurance"
              value={insuranceAnnual}
              onChange={setInsuranceAnnual}
              prefix="$"
            />

            <CalculatorInput
              label="Maintenance (% of value/yr)"
              value={maintenanceRate}
              onChange={setMaintenanceRate}
              suffix="%"
            />

            <CalculatorInput
              label="Annual Appreciation"
              value={appreciationRate}
              onChange={setAppreciationRate}
              suffix="%"
            />

            <Separator />

            <h2 className="text-lg font-semibold">Time Horizon</h2>

            <div className="space-y-1.5">
              <span className="text-sm font-medium">Hold Period</span>
              <div className="flex gap-2" role="group" aria-label="Hold Period">
                {[3, 5, 7, 10].map((years) => (
                  <button
                    key={years}
                    onClick={() => setHoldYears(years)}
                    className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      holdYears === years ? 'bg-amber-500 text-white' : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    {years} yrs
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-4 p-6">
            <h2 className="text-lg font-semibold">Comparison ({holdYears} years)</h2>

            <CalculatorResult
              label={buyingIsBetter ? 'Buying builds more wealth' : 'Renting may be better'}
              value={formatCurrency(Math.abs(equity - downPaymentGrowth))}
              highlight
              subtext={
                buyingIsBetter
                  ? 'More equity from buying vs investing down payment'
                  : 'Down payment grows more invested elsewhere'
              }
            />

            <Separator />

            <h2 className="text-lg font-semibold">Renting Costs</h2>

            <CalculatorResultsGrid>
              <CalculatorResult label="Total Rent Paid" value={formatCurrency(totalRentCost)} />
              <CalculatorResult
                label="Down Payment Invested"
                value={formatCurrency(downPaymentGrowth)}
                subtext={`${formatCurrency(downPayment)} at ${investmentReturn}%/yr`}
              />
            </CalculatorResultsGrid>

            <Separator />

            <h2 className="text-lg font-semibold">Buying Outcome</h2>

            <CalculatorResultsGrid>
              <CalculatorResult label="Monthly Payment" value={formatCurrency(totalMonthlyOwnership)} />
              <CalculatorResult label="Home Value" value={formatCurrency(futureValue)} />
              <CalculatorResult label="Remaining Mortgage" value={formatCurrency(remainingBalance)} />
              <CalculatorResult label="Equity Built" value={formatCurrency(equity)} highlight />
            </CalculatorResultsGrid>

            <div className="bg-muted/50 text-muted-foreground rounded-lg p-3 text-sm">
              <p>
                <strong>Note:</strong> This is a simplified comparison. Actual results depend on tax benefits, selling
                costs (5-6%), HOA fees, and market conditions. Use as a directional guide, not a precise forecast.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
